import React from 'react';
import { capitalize } from 'lodash';
import Form from 'mobx-react-form';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import {
  Grid,
  Typography,
  Box,
  Paper,
  Stack,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@mui/material';
import { observer } from 'mobx-react-lite';

import { UnitCountSection } from './UnitCountSection';
import SquareFootageTooltipText from './SquareFootageTooltipText';
import {
  COMMERCIAL_PROPERTY_SUBTYPES,
  COMMERCIAL_SUBTYPES_PROPER_CASE,
  ELEVATOR_TYPES,
  GENERAL_CONDITIONS_OPTIONS,
  PROPERTY_TYPES,
  propertyTypeOptions,
  SECTIONS,
  SF_BASIS_OPTIONS,
} from '../constants';
import NumericInput from 'components/shared/inputs/NumericInput';

export const SFAnalysisHelperText = 'Income approach calculations will be based on this measurement.';

type CommercialSubtypeKey = keyof typeof COMMERCIAL_SUBTYPES_PROPER_CASE;
type ElevatorTypeKey = keyof typeof ELEVATOR_TYPES;

const commercialPropertySubTypeOptions = COMMERCIAL_PROPERTY_SUBTYPES.map((value) => {
  return {
    value,
    label: COMMERCIAL_SUBTYPES_PROPER_CASE[value as CommercialSubtypeKey],
  };
});

const elevatorOptions = Object.keys(ELEVATOR_TYPES).map((key) => {
  return {
    label: capitalize(ELEVATOR_TYPES[key as ElevatorTypeKey]),
    value: ELEVATOR_TYPES[key as ElevatorTypeKey],
  };
});

const SF_BASIS_RADIO_BUTTON_ITEMS = Object.entries(SF_BASIS_OPTIONS).map(([acronym, fullString]) => ({
  value: acronym.toLowerCase(),
  label: `${fullString} (${acronym})`,
}));

type PropertyDescriptionProps = {
  form: Form;
};

const PropertyDescription = observer(({ form }: PropertyDescriptionProps) => {
  const values = form.values();
  const { propertyType, basisForSFAnalysis } = values?.property || {};

  const defaultBasisSelected = basisForSFAnalysis === 'gba';

  return (
    <Paper elevation={0} sx={{ bgcolor: 'transparent' }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Property Description
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={6}>
          <TextField
            {...form.$('property.buildingName').bind()}
            size="small"
            margin="normal"
            label="Building Name"
            fullWidth
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            {...form.$('property.buildingClass').bind()}
            size="small"
            margin="normal"
            label="Building Class"
            fullWidth
          />
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} sx={{ marginTop: '16px', marginBottom: '-16px' }}>
          <Typography variant="subtitle1">
            <strong>Building Description</strong>
          </Typography>
        </Grid>

        <Grid item xs={6}>
          <Box mb={2}>
            <FormControl margin="normal" fullWidth>
              <InputLabel id="propertyType-select-label">Property Type</InputLabel>
              <Select
                {...form.$('property.propertyType').bind()}
                labelId="propertyType-select-label"
                label="Property Type"
                size="small"
              >
                {propertyTypeOptions.map(({ label, value }) => (
                  <MenuItem key={value} value={value}>
                    {label || value}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <UnitCountSection
            form={form}
            fieldName={'property.residentialUnits'}
            section={SECTIONS.RESIDENTIAL_UNIT_COUNT}
            fieldLabel="Residential Units"
            propertyType={propertyType}
          />
          <UnitCountSection
            form={form}
            fieldName={'property.commercialUnits'}
            section={SECTIONS.COMMERCIAL_UNIT_COUNT}
            fieldLabel="Commercial Units"
            propertyType={propertyType}
          />
        </Grid>
        <Grid item xs={6}>
          {propertyType === PROPERTY_TYPES.COMMERCIAL && (
            <Box mb={2}>
              <FormControl margin="normal" fullWidth>
                <InputLabel id="propertySubtype-select-label">Property Subtype</InputLabel>
                <Select
                  {...form.$('property.propertySubtype').bind()}
                  labelId="propertySubtype-select-label"
                  label="Property Subtype"
                  size="small"
                  sx={{ minWidth: '100px' }}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 250,
                      },
                    },
                  }}
                >
                  {commercialPropertySubTypeOptions.map(({ label, value }) => (
                    <MenuItem key={value} value={value}>
                      {label || value}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}

          <NumericInput
            {...form.$('property.floors').bind()}
            size="small"
            margin="normal"
            fullWidth
            label="Floors"
            decimalScale={2}
            max={250}
            allowNegative={false}
          />

          <FormControl margin="normal" fullWidth>
            <InputLabel id="buildingType-select-label">Building Type</InputLabel>
            <Select
              {...form.$('property.buildingType').bind()}
              labelId="buildingType-select-label"
              label="Building Type"
              size="small"
              sx={{ minWidth: '100px' }}
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 250,
                  },
                },
              }}
            >
              {elevatorOptions.map(({ label, value }) => (
                <MenuItem key={value} value={value}>
                  {label || value}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 2, mb: 1 }}>
            <Typography variant="subtitle2">Basis for Square Foot Analysis</Typography>
            <Tooltip title={SquareFootageTooltipText}>
              <InfoOutlined sx={{ cursor: 'pointer' }} />
            </Tooltip>
          </Stack>

          <FormControl>
            <RadioGroup {...form.$('property.basisForSFAnalysis').bind()} row sx={{ minHeight: 40, mt: 0 }}>
              {SF_BASIS_RADIO_BUTTON_ITEMS.map(({ value, label }) => (
                <FormControlLabel control={<Radio />} key={value} sx={{ mb: 3 / 4 }} {...{ label, value }} />
              ))}
            </RadioGroup>
          </FormControl>

          {!defaultBasisSelected && (
            <Grid item xs={6}>
              <Box pr={1} mb={1}>
                <NumericInput
                  {...form.$('property.basisForSFAnalysisValue').bind()}
                  size="small"
                  margin="normal"
                  label={
                    basisForSFAnalysis
                      ? SF_BASIS_OPTIONS[basisForSFAnalysis.toUpperCase() as keyof typeof SF_BASIS_OPTIONS]
                      : ''
                  }
                  fullWidth
                  decimalScale={2}
                  allowNegative={false}
                  helperText={SFAnalysisHelperText}
                  thousandSeparator
                />
              </Box>
            </Grid>
          )}

          <Grid item xs={6}>
            <Box pr={1}>
              <NumericInput
                {...form.$('property.grossBuildingArea').bind()}
                size="small"
                margin="normal"
                label="Gross Building Area"
                fullWidth
                allowNegative={false}
                helperText={defaultBasisSelected ? SFAnalysisHelperText : ' '}
                thousandSeparator
              />
            </Box>
          </Grid>
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Box mt={2}>
            <Typography variant="subtitle1">
              <strong>Property Condition</strong>
            </Typography>
          </Box>

          <Grid item xs={6}>
            <FormControl margin="normal" fullWidth>
              <InputLabel id="buildingType-select-label">General Property Condition</InputLabel>
              <Select
                {...form.$('property.generalCondition').bind()}
                labelId="buildingType-select-label"
                label="General Property Condition"
                size="small"
                sx={{ minWidth: '100px' }}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 250,
                    },
                  },
                }}
              >
                {GENERAL_CONDITIONS_OPTIONS.map(({ label, value }) => (
                  <MenuItem key={value} value={value}>
                    {label || value}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Grid>
    </Paper>
  );
});

export { PropertyDescription };
